// Single source of truth for the chat model dropdown, in the order it shows
// them. Mirrors the keys in
// Plume-WSS/app/service/model_configs.py — keep them in sync. Each entry
// includes the model's `contextWindow` (max input + output tokens) so the
// dropdown can disable models that can't fit the active conversation, and
// `supportsImages` (mirrors that model's `supports_images`) so it can say which
// models read an attachment. The flag is positive but the BADGE is negative —
// nearly every model reads images, so the one that can't is the informative
// case. Keeping the flag opt-in means a model added here without it is treated
// as unable, which understates rather than overstates what it can do.

export const RESERVED_RESPONSE_TOKENS = 8192;

// localStorage key for the per-book model pick (owned by BrainstormTab's
// useLocalStorageState).
export const modelStorageKey = (bookId) => `model:selected:book:${bookId}`;

export const MODEL_CATALOG = [
  { id: "auto", name: "Auto", contextWindow: 200000 },
  {
    id: "GLM-5.3",
    // No supportsImages: model doesn't accept image input.
    name: "GLM 5.3",
    dateAdded: "2026-08-21",
    tags: ["NSFW"],
    contextWindow: 1000000,
  },
  {
    id: "GLM-5.3-Flash",
    // No supportsImages: model doesn't accept image input.
    name: "GLM 5.3 Flash",
    dateAdded: "2026-09-01",
    tags: ["NSFW"],
    contextWindow: 1000000,
  },
  {
    id: "Grok-4.6",
    supportsImages: true,
    name: "Grok 4.6",
    dateAdded: "2026-09-01",
    tags: ["NSFW"],
    contextWindow: 500000,
  },
  {
    id: "Kimi-K2.6",
    // No supportsImages: capable upstream, but our pinned provider endpoint
    // serves no image input, so an image turn never reaches it.
    name: "Kimi K2.6",
    dateAdded: "2026-04-28",
    tags: ["NSFW"],
    contextWindow: 256000,
  },
  {
    id: "Kimi-K2.5-Bedrock",
    supportsImages: true,
    name: "Kimi K2.5",
    dateAdded: "2026-05-07",
    tags: ["NSFW"],
    contextWindow: 256000,
  },
  {
    id: "GPT-5.6-Terra",
    supportsImages: true,
    name: "GPT-5.6 Terra",
    dateAdded: "2026-08-01",
    contextWindow: 1000000,
  },
  {
    // Legacy, demoted 2026-08-01.
    id: "GPT-5.4",
    supportsImages: true,
    name: "GPT-5.4",
    dateAdded: "2026-04-15",
    contextWindow: 400000,
  },
  {
    id: "Claude-Sonnet-5",
    supportsImages: true,
    name: "Claude Sonnet 5",
    dateAdded: "2026-08-01",
    contextWindow: 200000,
  },
  {
    // Legacy, demoted 2026-08-01.
    id: "Claude-Sonnet-4.6",
    supportsImages: true,
    name: "Claude Sonnet 4.6",
    dateAdded: "2026-04-15",
    contextWindow: 200000,
  },
  {
    id: "Gemini-3.8-Flash",
    // No supportsImages: capable upstream, but image input is unprobed on our
    // path (mirrors supports_images=False in model_configs.py).
    name: "Gemini 3.8 Flash",
    dateAdded: "2026-09-04",
    // No NSFW tag: it refused or faded to black on the explicit-turn eval
    // scenarios, so the server routes those away from it.
    contextWindow: 1000000,
  },
  {
    // Legacy, demoted 2026-08-01.
    id: "Gemini-3.1-Pro",
    supportsImages: true,
    name: "Gemini 3.1 Pro",
    dateAdded: "2026-04-15",
    contextWindow: 1000000,
  },
];

// Cheap client-side token estimate. The agent loop on the server uses tiktoken
// for the real count; this is just for "would this fit?" UI hints.
export function estimateTokensFromText(text) {
  if (!text) return 0;
  return Math.ceil(text.length / 4);
}

// True if the author has attached an image this model can't read. Same shape as
// isModelTooSmall so the dropdown disables both for the same reason: the turn
// would silently not do what the user is asking for.
export function isModelImageBlind(model, hasPendingImages) {
  if (!model || model.id === "auto") return false;
  if (!hasPendingImages) return false;
  return !model.supportsImages;
}

// True if the conversation already overflows this model's input cap, leaving
// no room for a meaningful response.
export function isModelTooSmall(model, estimatedInputTokens) {
  if (!model || model.id === "auto") return false;
  if (!model.contextWindow) return false;
  if (!estimatedInputTokens) return false;
  return model.contextWindow * 0.95 < estimatedInputTokens + RESERVED_RESPONSE_TOKENS;
}
